import React, { useState } from 'react'
import { Container, Card } from 'react-bootstrap'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { authAPI } from '../../api'
import logger from '../../utils/logger'
import { getErrorMessage } from '../../utils/errors'
import StyledAlert from '../../components/StyledAlert'
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter'
import { StyledPasswordInput } from '../../components/StyledPasswordInput'
import { resetPasswordSchema, type ResetPasswordFormData } from '../../schemas/authSchema'
import {
  FormGroup,
  FieldError,
  PrimaryButton,
  colors,
  gradients,
  shadows
} from '../../theme/sharedComponents'

const PageWrapper = styled.div`
  flex: 1;
  background: ${colors.background};
  display: flex;
  align-items: center;
  padding: 40px 0;
`

const StyledCard = styled(Card)`
  background: ${colors.background};
  border: 1px solid ${colors.primary};
  box-shadow: ${shadows.card};
  max-width: 500px;
  margin: 0 auto;

  .card-header {
    background: ${gradients.primary};
    border-bottom: none;
    color: ${colors.textLight};
    padding: 1.5rem;
    text-align: center;

    h2 {
      margin: 0;
      font-size: 1.75rem;
      font-weight: 600;
    }

    .subtitle {
      margin-top: 0.5rem;
      font-size: 0.95rem;
      opacity: 0.9;
    }
  }

  .card-body {
    padding: 2rem;
  }

  .form-label {
    color: ${colors.textLightMode};
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .form-control {
    background: ${colors.backgroundAlt};
    border: 1px solid ${colors.borderLight};
    color: ${colors.textLightMode};
    padding: 0.75rem;
    border-radius: var(--radius);

    &:focus {
      background: ${colors.backgroundAlt};
      border-color: ${colors.primary};
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
      color: ${colors.textLightMode};
    }

    &::placeholder {
      color: ${colors.textLightModeMuted};
    }
  }

  .back-link {
    color: ${colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    transition: color 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      color: ${colors.primaryDark};
      text-decoration: underline;
    }
  }

  .password-requirements {
    color: ${colors.textLightModeMuted};
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }
`

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const password = watch('password', '')

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.')
      return
    }

    setLoading(true)
    setError('')

    try {
      await authAPI.resetPassword(token, data.password)
      setSuccess(true)

      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/')
      }, 3000)
    } catch (err: unknown) {
      logger.error('Reset password error:', err)
      setError(getErrorMessage(err, 'An error occurred. The link may have expired. Please request a new password reset.'))
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <PageWrapper>
        <Container>
          <StyledCard>
            <Card.Header>
              <h2><i className="bi bi-x-circle me-2"></i>Invalid Link</h2>
            </Card.Header>
            <Card.Body>
              <StyledAlert variant="danger">
                <strong>Invalid or Expired Link</strong>
                This password reset link is invalid or has expired.
                Please request a new password reset.
              </StyledAlert>
              <div className="text-center">
                <Link to="/forgot-password" className="back-link">
                  <i className="bi bi-arrow-left"></i>
                  Request New Reset Link
                </Link>
              </div>
            </Card.Body>
          </StyledCard>
        </Container>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <Container>
        <StyledCard>
          <Card.Header>
            <h2><i className="bi bi-shield-lock me-2"></i>Reset Password</h2>
            <div className="subtitle">Enter your new password</div>
          </Card.Header>
          <Card.Body>
            {error && (
              <StyledAlert variant="danger" className="mb-3">
                <strong>Password Reset Failed</strong>
                {error}
              </StyledAlert>
            )}

            {success ? (
              <StyledAlert variant="success">
                <strong><i className="bi bi-check-circle me-2"></i>Password Reset!</strong>
                <p className="mb-2">
                  Your password has been successfully reset. You can now log in with your new password.
                  Redirecting you to the home page...
                </p>
                <div className="text-center mt-3">
                  <Link to="/" className="back-link">
                    <i className="bi bi-arrow-left"></i>
                    Back to Home
                  </Link>
                </div>
              </StyledAlert>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <StyledPasswordInput
                    {...register('password')}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    maxLength={128}
                  />
                  <PasswordStrengthMeter password={password} />
                  {errors.password && <FieldError>{errors.password.message}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <StyledPasswordInput
                    {...register('confirmPassword')}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    maxLength={128}
                  />
                  {errors.confirmPassword && <FieldError>{errors.confirmPassword.message}</FieldError>}
                </FormGroup>

                <div className="d-grid mb-3">
                  <PrimaryButton
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resetting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Reset Password
                      </>
                    )}
                  </PrimaryButton>
                </div>

                <div className="text-center">
                  <Link to="/" className="back-link">
                    <i className="bi bi-arrow-left"></i>
                    Back to Home
                  </Link>
                </div>
              </form>
            )}
          </Card.Body>
        </StyledCard>
      </Container>
    </PageWrapper>
  )
}

export default ResetPasswordPage
